import React, { useCallback, useMemo, useState } from "react";
import { Row } from "@tanstack/react-table";
import { DataTable } from "ui-components/data-table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { PlanningFreightCategory, PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { usePlanningFreightList } from "../hooks/usePlanningFreightList";
import {
  useDrawerTableContainerHeight,
} from "../hooks/useDrawerTableContainerHeight";
import {
  getOutboundPlanningShipmentColumns,
  getRowCanExpandFreight,
  OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningShipmentColumns";
import PlanningFreightListTotalsBar from "./planningFreightListTotalsBar";
import {
  getOutboundPlanningItemColumns,
  OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
  OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT,
} from "../columns/outboundPlanningItemColumns";
import "../outboundPlanningList.scss";

const MAIN_ROW_HEIGHT = 44;
const ITEM_ROW_HEIGHT = 36;

interface PlanningFreightListPanelProps {
  category: PlanningFreightCategory;
  originTerminalNumber: number;
  destinationTerminalNumbers: number[];
  fetchId: string;
}

function useColumnState(
  options: { id: string; label: string }[],
  hiddenByDefault: string[]
) {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hiddenSet = new Set(hiddenByDefault);
    return options.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hiddenSet.has(id) }),
      {} as Record<string, boolean>
    );
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => options.map(({ id }) => id));
  return { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder };
}

const PlanningFreightListPanel: React.FC<PlanningFreightListPanelProps> = ({
  category,
  originTerminalNumber,
  destinationTerminalNumbers,
  fetchId,
}) => {
  const { containerRef, tableHeight } = useDrawerTableContainerHeight(24);
  const { data, isLoading, isError } = usePlanningFreightList(
    category,
    originTerminalNumber,
    destinationTerminalNumbers,
    fetchId,
    true
  );

  const shipmentColumnState = useColumnState(
    OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS,
    OUTBOUND_PLANNING_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT
  );
  const itemColumnState = useColumnState(
    OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS,
    OUTBOUND_PLANNING_ITEM_COLUMNS_HIDDEN_BY_DEFAULT
  );

  const shipmentColumns = useMemo(
    () =>
      getOutboundPlanningShipmentColumns({
        category,
        originTerminalNumber,
      }),
    [category, originTerminalNumber]
  );

  const itemColumns = useMemo(() => getOutboundPlanningItemColumns(), []);

  const renderSubComponent = useCallback(
    (row: Row<PlanningFreightProbill>) => (
      <div className="outbound-planning-freight-list-panel__sub-table">
        <DataTable
          columns={itemColumns}
          data={row.original.items ?? []}
          columnVisibility={itemColumnState.columnVisibility}
          onColumnVisibilityChange={itemColumnState.setColumnVisibility}
          columnOrder={itemColumnState.columnOrder}
          onColumnOrderChange={itemColumnState.setColumnOrder}
          showColumnSelector
          columnSelectorOptions={OUTBOUND_PLANNING_ITEM_COLUMN_OPTIONS}
          enableColumnResize
          enableColumnReorder
          enableRowSelection={false}
          responsive
          autoHeight={false}
          shrinkToContent
          rowHeight={ITEM_ROW_HEIGHT}
          noResultsText="No items for this probill"
        />
      </div>
    ),
    [itemColumns, itemColumnState]
  );

  return (
    <div className="outbound-planning-freight-list-panel">
      <div ref={containerRef} className="outbound-planning-freight-list-panel__table">
        {isError ? (
          <XGSErrorMessage>Error loading freight list. Please try again.</XGSErrorMessage>
        ) : isLoading ? (
          <div className="outbound-planning-freight-list-panel__loading">
            <Loading isLoading />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="outbound-planning-freight-list-panel__empty">No shipments found.</div>
        ) : (
          <DataTable
            columns={shipmentColumns}
            data={data}
            isLoading={false}
            columnVisibility={shipmentColumnState.columnVisibility}
            onColumnVisibilityChange={shipmentColumnState.setColumnVisibility}
            columnOrder={shipmentColumnState.columnOrder}
            onColumnOrderChange={shipmentColumnState.setColumnOrder}
            showColumnSelector
            columnSelectorOptions={OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS}
            enableColumnResize
            enableColumnReorder
            enableRowSelection={false}
            responsive
            sorting
            enableMultiSort
            filters
            renderSubComponent={renderSubComponent}
            getRowCanExpand={getRowCanExpandFreight}
            stickyLeadingColumnId="expand"
            autoHeight={false}
            listHeightOverride={tableHeight ?? 400}
            rowHeight={MAIN_ROW_HEIGHT}
            stickyHeaders
            renderTableFooter={({ rows }) => (
              <PlanningFreightListTotalsBar rows={rows} rowHeight={MAIN_ROW_HEIGHT} />
            )}
            customHeaderRowClass="outbound-planning-shipment-table__header-row"
          />
        )}
      </div>
    </div>
  );
};

export default PlanningFreightListPanel;
